/**
 * SubscriptionContext - Centralny context do zarządzania subskrypcjami
 *
 * Pobiera status subskrypcji firmy raz i udostępnia go wszystkim komponentom.
 * Eliminuje potrzebę wielokrotnego pobierania danych z API.
 */

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import api from '../services/apiService';
import { UserContext } from './UserContext';
import { SUBSCRIPTION_KEYS, hasAccessToMenuItem, hasAccessToRoute, filterMenuBySubscriptions } from '../config/subscriptionConfig';

export const SubscriptionContext = createContext();

// A failed status request must not look like "the company has no subscription" — that hid every
// module whenever the endpoint failed (PROD 2026-11-03, a 500 for companies ≤ 30 days from the end).
// So: retry server/network errors, then keep the last known status (this session or the last one
// saved in this browser); with nothing known, `unavailable` tells the guards to say so and offer
// a retry instead of redirecting. The server enforces subscriptions on its own either way.
const EMPTY_STATUS = {
  has_any_subscription: false,
  subscription_checks: {},
  active_subscriptions: [],
  subscription_types: [],
};
const RETRY_DELAYS_MS = [1500, 4000];
const CACHE_PREFIX = 'entrigy_subscription_status:';
const readCachedStatus = (userId) => {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + userId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeCachedStatus = (userId, data) => {
  try {
    window.localStorage.setItem(CACHE_PREFIX + userId, JSON.stringify(data));
  } catch {
    // storage blocked or full: the in-memory copy still covers this session
  }
};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// A 4xx (e.g. 400 "no associated company") is a real answer, not an outage.
const isOutage = (err) => {
  const code = err?.response?.status;
  return !code || code >= 500 || code === 429;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, loading: userLoading } = useContext(UserContext);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const lastKnown = useRef(null);   // { userId, data } of the last successful answer

  // Pobierz status subskrypcji
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!user?.id || !user?.accessToken) {
      // If UserContext finished loading and there's no user, stop loading
      // This allows RouteGuard/PrivateRoute to redirect to login
      if (!userLoading) {
        setLoading(false);
      }
      return;
    }

    const userId = user.id;
    try {
      setLoading(true);
      setError(null);
      let response = null;
      let lastError = null;
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          response = await api.get('/company/subscription-status/');
          break;
        } catch (err) {
          lastError = err;
          if (!isOutage(err) || attempt === RETRY_DELAYS_MS.length) break;
          await wait(RETRY_DELAYS_MS[attempt]);
        }
      }
      if (response) {
        lastKnown.current = { userId, data: response.data };
        writeCachedStatus(userId, response.data);
        setSubscriptionStatus(response.data);
        setUnavailable(false);
        return;
      }
      console.error('Error fetching subscription status:', lastError);
      setError(lastError?.message || 'Failed to load subscription status');
      if (!isOutage(lastError)) {
        setSubscriptionStatus(EMPTY_STATUS);
        setUnavailable(false);
        return;
      }
      const known = (lastKnown.current?.userId === userId && lastKnown.current.data) || readCachedStatus(userId);
      setSubscriptionStatus(known || EMPTY_STATUS);
      setUnavailable(!known);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.accessToken, userLoading]);

  // Pobierz status przy montowaniu i gdy zmieni się użytkownik
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Refresh subscription status (można wywołać po zmianie subskrypcji)
  const refreshSubscriptionStatus = useCallback(() => {
    return fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  /**
   * Sprawdza czy firma ma konkretną subskrypcję
   * @param {number} typeId - ID typu subskrypcji (z SUBSCRIPTION_TYPES)
   * @returns {boolean}
   */
  const hasSubscription = useCallback((typeId) => {
    if (loading || !subscriptionStatus?.subscription_checks) {
      return false;
    }
    const key = SUBSCRIPTION_KEYS[typeId];
    return subscriptionStatus.subscription_checks[key] === true;
  }, [loading, subscriptionStatus]);

  /**
   * Sprawdza czy firma ma KTÓRĄKOLWIEK z podanych subskrypcji
   * @param {number[]} typeIds - Tablica ID typów subskrypcji
   * @returns {boolean}
   */
  const hasAnySubscription = useCallback((typeIds = []) => {
    if (typeIds.length === 0) {
      return subscriptionStatus?.has_any_subscription || false;
    }
    return typeIds.some(typeId => hasSubscription(typeId));
  }, [hasSubscription, subscriptionStatus]);

  /**
   * Sprawdza czy firma ma WSZYSTKIE podane subskrypcje
   * @param {number[]} typeIds - Tablica ID typów subskrypcji
   * @returns {boolean}
   */
  const hasAllSubscriptions = useCallback((typeIds) => {
    return typeIds.every(typeId => hasSubscription(typeId));
  }, [hasSubscription]);

  /**
   * Sprawdza czy użytkownik ma dostęp do elementu menu
   * @param {Object} menuItem - Element z ADMIN_MENU lub USER_MENU
   * @returns {boolean}
   */
  const canAccessMenuItem = useCallback((menuItem) => {
    return hasAccessToMenuItem(menuItem, subscriptionStatus?.subscription_checks, user);
  }, [subscriptionStatus, user]);

  /**
   * Sprawdza czy użytkownik ma dostęp do trasy
   * @param {string} pathname - Ścieżka do sprawdzenia
   * @returns {boolean}
   */
  const canAccessRoute = useCallback((pathname) => {
    return hasAccessToRoute(pathname, subscriptionStatus?.subscription_checks);
  }, [subscriptionStatus]);

  /**
   * Filtruje menu na podstawie subskrypcji
   * @param {Array} menu - Tablica elementów menu
   * @returns {Array} - Przefiltrowane menu
   */
  const filterMenu = useCallback((menu) => {
    return filterMenuBySubscriptions(menu, subscriptionStatus?.subscription_checks, user);
  }, [subscriptionStatus, user]);

  /**
   * Pobiera listę aktywnych typów subskrypcji
   * @returns {Object[]}
   */
  const getActiveSubscriptionTypes = useCallback(() => {
    return subscriptionStatus?.subscription_types || [];
  }, [subscriptionStatus]);

  /**
   * Pobiera szczegóły subskrypcji (daty wygaśnięcia itp.)
   * @returns {Object[]}
   */
  const getActiveSubscriptions = useCallback(() => {
    return subscriptionStatus?.active_subscriptions || [];
  }, [subscriptionStatus]);

  const value = {
    // Stan
    subscriptionStatus,
    loading,
    error,
    unavailable,   // status could not be loaded and none is known: don't treat as "no subscription"

    // Podstawowe sprawdzenia
    hasSubscription,
    hasAnySubscription,
    hasAllSubscriptions,

    // Sprawdzenia dla menu i tras
    canAccessMenuItem,
    canAccessRoute,
    filterMenu,

    // Gettery
    getActiveSubscriptionTypes,
    getActiveSubscriptions,

    // Akcje
    refreshSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
